import { cn } from '@/utils/cn';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

interface MenuProps {
  sections: {
    key: React.ReactNode;
    label: string;
    disabled?: boolean;
    notificationCount?: number;
  }[];
  onSectionChange?: (index: number) => void;
  className?: string;
}

export const Menu = ({ sections, onSectionChange, className }: MenuProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = parseInt(searchParams.get('section') || '0', 10);

  useEffect(() => {
    onSectionChange?.(activeSection);
  }, [activeSection, onSectionChange]);

  const handleItemClick = (index: number) => {
    setSearchParams(
      prev => {
        prev.set('section', index.toString());
        return prev;
      },
      { replace: true },
    );
  };

  return (
    <div className={cn('w-full', className)}>
      <nav className="overflow-x-auto overflow-y-hidden border-b border-gray-200">
        <div className="-mb-px flex min-w-max">
          {sections.map((section, index: number) => (
            <button
              key={index}
              onClick={() => {
                handleItemClick(index);
                window.scrollTo(0, window.scrollY);
              }}
              className={cn(
                'relative flex min-w-0 items-center gap-1.5 overflow-visible border-b-2 px-6 py-3 text-sm font-medium',
                activeSection === index
                  ? 'border-linko-light text-linko-light'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                section.disabled &&
                  'cursor-not-allowed opacity-50 hover:border-transparent hover:text-gray-500',
              )}
              disabled={section.disabled}
            >
              <span>{t(section.label)}</span>
              {section.notificationCount && section.notificationCount > 0 && (
                <span className="flex size-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white shadow-sm">
                  {section.notificationCount > 9
                    ? '9+'
                    : section.notificationCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};
